import { useAppSelector } from '@/store/hooks';
import { selectReportingV2Widget } from '@/store/slices/reportingV2/reportingV2Slice';
import { Box, Typography } from '@mui/material';
import { dimensionLabel, num, ratio, sharePercent, sumOf } from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { COLORS } from '../theme';
import WidgetCard from './WidgetCard';
import WidgetExportButton from './WidgetExportButton';

interface IProps {
  widgetKey: string;
  title: string;
  labelKey: string;
  valueKey: string;
  /** When provided, clicking a bar toggles that value in the filter. */
  selectedValues?: string[];
  onSelect?: (value: string) => void;
  /**
   * Display-only relabelling (e.g. the device taxonomy's "TV" → "CTV" so it
   * agrees with the Channel card next to it). The raw value is still what gets
   * sent as the filter.
   */
  labelFn?: (value: unknown) => string;
  /**
   * Shown INSTEAD of the bars when every row resolves to "Unmapped" — i.e. the
   * dimension carries no value at all for the current selection. A single
   * "Unmapped 100%" bar is technically accurate but reads as a rendering bug,
   * and it got reported as one; saying why is the same information, minus the
   * false alarm.
   */
  unmappedNote?: string;
}

/**
 * Share of the total that has to be missing before the card discloses it. Below
 * this, the hidden slice is a handful of stray nulls and the note is more
 * alarming than the gap.
 *
 * 1%, not 5%: a whole platform can go missing and still land under 5%. Bonneville's
 * device breakdown hid 3,719,192 impressions — every Simpli.fi row — at 4.6%, and
 * showed Mobile/Desktop/CTV/Tablet summing to 100% with nothing saying otherwise.
 * The threshold is meant to separate rounding noise from a systematically absent
 * source, and a concentrated miss is systematic whatever its share.
 */
const HIDDEN_SHARE_THRESHOLD = 0.01;

/** Horizontal proportional-bar list — the donut/bar widgets (channel, device, …). */
const BreakdownCard = ({
  widgetKey,
  title,
  labelKey,
  valueKey,
  selectedValues,
  onSelect,
  labelFn = dimensionLabel,
  unmappedNote,
}: IProps) => {
  const { rows, loading, error } = useAppSelector(selectReportingV2Widget(widgetKey));
  const visibleRows = rows.filter(row => {
    const label = row[labelKey];
    const mapped = dimensionLabel(label);
    // Drop empty Unmapped-only noise when other mapped rows exist.
    if (mapped !== 'Unmapped') return true;
    return rows.every(r => dimensionLabel(r[labelKey]) === 'Unmapped');
  });
  const total = sumOf(visibleRows, valueKey);
  const interactive = !!onSelect;
  // Everything unmapped means the dimension is unpopulated for this selection,
  // not that one bucket happens to dominate.
  const allUnmapped = visibleRows.length > 0 && visibleRows.every(r => dimensionLabel(r[labelKey]) === 'Unmapped');

  // Hiding the Unmapped bar keeps the card readable, but the percentages are
  // then shares of what's left, not of delivery. Measured on dev: channel is
  // populated for Beeswax only, so "CTV 71%" was 71% of the 34% that has a
  // channel — 299M impressions dropped with nothing on screen saying so.
  // Disclose the basis once the hidden slice stops being rounding noise.
  const grandTotal = sumOf(rows, valueKey);
  const hiddenShare = grandTotal > 0 ? (grandTotal - total) / grandTotal : 0;
  const showHiddenNote = !allUnmapped && hiddenShare >= HIDDEN_SHARE_THRESHOLD;
  const onExportCsv = useExportWidgetCsv(widgetKey, rows);

  return (
    <WidgetCard
      title={title}
      headerAction={<WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />}
      loading={loading}
      error={error}
      isEmpty={visibleRows.length === 0}
    >
      {allUnmapped && unmappedNote ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
          {unmappedNote}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }} role={interactive ? 'listbox' : undefined}>
          {visibleRows.slice(0, 10).map((row, index) => {
            // `rawLabel` is the value the backend knows; `display` may be
            // relabelled for the user. Filter on the former, show the latter.
            const rawLabel = dimensionLabel(row[labelKey]);
            const display = labelFn(row[labelKey]);
            const filterValue = rawLabel === 'Unmapped' ? String(row[labelKey] ?? '') : rawLabel;
            // An Unmapped row backed by null yields '', which still ships as a
            // real filter value: zero rows come back, no chip lights up, and the
            // row you'd click to undo it disappears. Leave those rows inert.
            const selectable = interactive && filterValue !== '';
            const selected = selectedValues?.includes(display) || selectedValues?.includes(filterValue);
            const activate = () => {
              if (selectable) onSelect?.(filterValue);
            };
            return (
              <Box
                key={`${display}-${index}`}
                role={selectable ? 'option' : undefined}
                aria-selected={selectable ? selected : undefined}
                tabIndex={selectable ? 0 : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  cursor: selectable ? 'pointer' : 'default',
                  borderRadius: 1,
                  px: interactive ? 0.5 : 0,
                  mx: interactive ? -0.5 : 0,
                  py: 0.25,
                  bgcolor: selected ? `${COLORS.primary}14` : 'transparent',
                  '&:hover': interactive ? { bgcolor: `${COLORS.primary}09` } : {},
                  '&:focus-visible': interactive ? { outline: `2px solid ${COLORS.primary}`, outlineOffset: 2 } : {},
                  transition: 'background-color 0.15s',
                }}
                onClick={interactive ? activate : undefined}
                onKeyDown={
                  interactive
                    ? e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          activate();
                        }
                      }
                    : undefined
                }
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography
                    variant='body2'
                    title={display}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: selected ? 600 : 400,
                      color: selected ? COLORS.primary : 'inherit',
                    }}
                  >
                    {display}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {sharePercent(row[valueKey], total)}
                  </Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.border }}>
                  <Box
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: COLORS.primary,
                      opacity: selected ? 1 : 0.7,
                      // Scale to the same basis as the sharePercent label above, else the
                      // top row is always a full-width bar labelled e.g. "45%".
                      width: `${ratio(row[valueKey], total) * 100}%`,
                      minWidth: 2,
                      transition: 'opacity 0.15s',
                    }}
                  />
                </Box>
              </Box>
            );
          })}
          {showHiddenNote && (
            <Typography variant='caption' sx={{ color: COLORS.muted, mt: 0.5 }}>
              Shares are of the {sharePercent(total, grandTotal)} of impressions that carry a{' '}
              {title.replace(/^Impressions by /, '').toLowerCase()}. {num(grandTotal - total)} (
              {sharePercent(grandTotal - total, grandTotal)}) aren’t counted here.
              {unmappedNote ? ` ${unmappedNote}` : ''}
            </Typography>
          )}
        </Box>
      )}
    </WidgetCard>
  );
};

export default BreakdownCard;
